'use client';

import { useEffect, useState } from 'react';
import { stegaClean } from '@sanity/client/stega';
import { cn } from '@/lib/utils';

/**
 * Figma 7366:154. Pairs the Sanity titles, in order, with the sections it
 * finds in the DOM via `[data-chapter]`.
 */
export default function ChapterIndex({ titles }: { titles?: string[] }) {
  const [active, setActive] = useState(0);
  const items = (titles ?? []).map((title) => stegaClean(title));

  useEffect(() => {
    if (!items.length) return;

    const sections = Array.from(
      document.querySelectorAll<HTMLElement>('[data-chapter]'),
    );
    if (!sections.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;

        const index = sections.indexOf(visible.target as HTMLElement);
        if (index >= 0) setActive(index);
      },
      { rootMargin: '-40% 0px -40% 0px', threshold: [0, 0.25, 0.5, 1] },
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, [items.length]);

  if (!items.length) return null;

  return (
    <nav
      aria-label="Chapters"
      // Figma places the list 36px under the ticker with 64px above its first
      // row. The float's box is h-0 but its padding and margin still count,
      // so the negative bottom margin zeroes it out and nothing below moves.
      className="hidden lg:flex sticky top-0 z-[20] float-left h-0 flex-col items-start mt-[4.5rem] pt-[8rem] -mb-[12.5rem] ps-[5.125rem] mix-blend-difference pointer-events-none"
    >
      {items.map((title, index) => (
        <a
          key={title + index}
          href={`#chapter-${index + 1}`}
          className={cn(
            'pointer-events-auto flex shrink-0 items-center gap-[1.5rem] px-[1.25rem] py-[1rem] font-geist text-[2rem] font-medium leading-[1.3125] no-underline whitespace-nowrap transition-colors duration-300 border-l-[0.25rem]',
            index === active
              ? 'text-white border-white'
              : 'text-white/40 border-transparent hover:text-white/70',
          )}
        >
          <span className="font-geist-mono shrink-0">
            [{String(index + 1).padStart(2, '0')}]
          </span>
          <span>{title}</span>
        </a>
      ))}
    </nav>
  );
}
